// -----------------------------------------------------------------------------
// src/pages/HelpCenter.js   – 2025-06-17  (Apple-compliant Support URL)
// FULL component – copy-paste ready
// -----------------------------------------------------------------------------
import React from 'react';
import { Box, Typography, Container, Toolbar, AppBar, Tabs, Tab, Link, Grid, Paper, Divider,  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import HomeHeader from '../../Components/HomeHeader';
import HomeFooter from '../../Components/HomeFooter';

/* ───────── help topics (videos + blurb) ───────── */
const helpTopics = [
  {
    label: 'Dashboard',
    video: 'https://www.youtube.com/embed/JtMUVsqadUI?si=fMtBoNZKFuwe6Jq2',
    text: 'View and manage your dashboard.',
  },
  {
    label: 'Marketplace',
    video: 'https://www.youtube.com/embed/ck_YtUrSaTQ?si=q__CJn3jO6VCvJlf',
    text: 'Explore our creator selection and launch projects.',
  },
  {
    label: 'Campaigns',
    video: 'https://www.youtube.com/embed/vBEEWHIv3ms?si=EprBNuvQnmELE4Og',
    text: 'Track and optimize all live campaigns.',
  },
  {
    label: 'Partnerships',
    video: 'https://www.youtube.com/embed/xwSKrV88u0U?si=YF92kmgSR158LHhM',
    text: 'Manage long-term brand / creator collaborations.',
  },
  {
    label: 'Blitz Pay',
    video: 'https://www.youtube.com/embed/mm1Xqrr_eng?si=D4vAtn_E9ZVDiVk7',
    text: 'Send instant payments with Blitz Pay.',
  },
  {
    label: 'Trackers',
    video: 'https://www.youtube.com/embed/Qsr5Jv5LaaM?si=ZdUtU2yzxwW8R7Lf',
    text: 'Monitor deliverables and performance in real time.',
  },
  {
    label: 'Creator Management',
    video: 'https://www.youtube.com/embed/3ErrbUj0j6Q?si=ahkKvqkXCQs2VVBn',
    text: 'On-board and oversee your roster of creators.',
  },
];

/* ───────── styled tab to shrink spacing ───────── */
const StyledTab = styled(Tab)(({ theme }) => ({
  minWidth: 0,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  fontSize: '0.875rem',
  textTransform: 'none',
}));

export default function HelpCenter() {
  const theme = useTheme();
  const [selected, setSelected] = React.useState(0);
  const topic = helpTopics[selected];

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        color: 'text.primary',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <HomeHeader />
      <Toolbar />

      <Container maxWidth="lg" sx={{ pt: 4, pb: 8, flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" gutterBottom>
            Help Center
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Pay · Create · Collaborate
          </Typography>
        </Box>

        <Divider />

        {/* Tabs */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            mt: 4,
          }}
        >
          <Tabs
            value={selected}
            onChange={(_, v) => setSelected(v)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="inherit"
            indicatorColor="primary"
          >
            {helpTopics.map(({ label }) => (
              <StyledTab key={label} label={label} />
            ))}
          </Tabs>
        </AppBar>

        {/* Main content */}
        <Grid container spacing={4} sx={{ mt: 1 }}>
          {/* Video & text */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                bgcolor: 'background.paper',
                color: 'text.primary',
              }}
            >
              <Typography variant="h5" gutterBottom>
                {topic.label}
              </Typography>

              <Box
                component="iframe"
                src={topic.video}
                title={`${topic.label} tutorial`}
                sx={{
                  width: '100%',
                  height: { xs: 240, md: 360 },
                  border: 0,
                  mb: 2,
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />

              <Typography>{topic.text}</Typography>
            </Paper>
          </Grid>

          {/* Contact-us side panel */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                bgcolor: 'background.paper',
                color: 'text.primary',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Need more help?
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
                Email our support team and we’ll get back within one business
                day.
              </Typography>

              <Link
                href="mailto:win@useblitz.co"
                underline="always"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                win@useblitz.co
              </Link>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">Business Hours</Typography>
              <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
                Monday – Friday, 9 am – 6 pm CT
              </Typography>

              <Typography variant="subtitle2">Response Time</Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>&lt; 24 hours</strong> for all support tickets.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <HomeFooter />
    </Box>
  );
}
